import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LegalEntity } from '@ms-organization-entities';
import { GlobalMeasureChecksService, LegalEntityService } from '@pt/api-client';
import {
  CreateRiskAssessmentPayload,
  DuplicateRiskAssessmentPayload,
} from '../../../models';
import {
  IconType,
  IoniconsOutline,
  NavigationData,
  PtUiAppRoutes,
  REQUIRED_VALIDATOR_KEY,
} from '@pt/pt-models';
import { ListLegalEntityForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/legalentity/ListLegalEntityForMenuResponse';
import { RiskAssessmentActions, ValidatorGenerator } from '@pt/pt-shared';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RiskAssessmentOverrideOnDuplicate } from '@primatoo-ms-organization';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { PtCoreActions, PtCoreSelectors } from '@pt/pt-core';
import { CreateGlobalMeasureDocRequest } from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheckdoc/CreateGlobalMeasureDocRequest';
import { Observable } from 'rxjs';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { takeUntil } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'pt-create-risk-assessment-dialog',
  templateUrl: './create-risk-assessment-dialog.component.html',
  styles: [
    `
      .ng-select-no-border .ng-select-container {
        border-radius: 0 !important;
      }

      .loader-container {
        height: 175px;
        display: grid;
        place-items: center;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CreateRiskAssessmentDialogComponent implements OnInit {
  @Input() openOnSuccess = true;
  @Input() dialogMode: 'CREATE' | 'DUPLICATE' = 'CREATE';
  @Input() parentId?: number;
  @Input() createRiskAssessmentPayload: CreateRiskAssessmentPayload;
  @Input() duplicateRiskAssessmentPayload: DuplicateRiskAssessmentPayload;
  public org$: Observable<Organization> = this.store.select(
    PtCoreSelectors.organization
  );
  private editInternalIdEnable = false;

  public legalEntities: LegalEntity[] = [];
  public folders: NavigationData[] = [];
  public IconClose = IoniconsOutline.close;
  public loading = false;
  public SVG = IconType.SVG;
  public form: FormGroup;
  public enabledTempate = false;

  public REQUIRED = ValidatorGenerator.generateWidgetValidator(
    REQUIRED_VALIDATOR_KEY
  );
  public formGroup: FormGroup = new FormGroup({
    name: new FormControl(null, [Validators.required]),
    internalId: new FormControl(null),
    description: new FormControl(null),
    legalEntityIds: new FormControl([]),
  });
  public fields: string[] = [];

  constructor(
    private store: Store,
    public activeModal: NgbActiveModal,
    private actionsSubject: ActionsSubject,
    private legalEntityService: LegalEntityService,
    private riskAssessmentsService: GlobalMeasureChecksService
  ) {}

  setLoading(value: boolean, withDelay = false) {
    if (!withDelay) {
      this.loading = value;
    } else {
      setTimeout(() => {
        this.loading = value;
      }, 300);
    }
  }

  ngOnInit(): void {
    this.setLoading(true);

    this.org$.pipe(untilDestroyed(this)).subscribe((org) => {
      this.editInternalIdEnable = true; // org.editInternalIdEnable;
      this.alignDisabled();
    });

    this.actionsSubject
      .pipe(
        ofType(RiskAssessmentActions.createRiskAssessmentSuccess),
        untilDestroyed(this)
      )
      .subscribe((action) => {
        if (this.openOnSuccess) {
          this.store.dispatch(
            PtCoreActions.navigateTo({
              route: [
                PtUiAppRoutes.BUSINESS_MANAGEMENT,
                PtUiAppRoutes.GLOBAL_MEASURE_CHECK,
                PtUiAppRoutes.EDIT,
                action?.id.toString(),
              ],
            })
          );
        }
        this.activeModal.close(true);
      });

    this.actionsSubject
      .pipe(
        ofType(RiskAssessmentActions.duplicateRiskAssessmentSuccess),
        untilDestroyed(this)
      )
      .subscribe((action) => {
        if (this.openOnSuccess) {
          this.store.dispatch(
            PtCoreActions.navigateTo({
              route: [
                PtUiAppRoutes.BUSINESS_MANAGEMENT,
                PtUiAppRoutes.GLOBAL_MEASURE_CHECK,
                PtUiAppRoutes.EDIT,
                action?.response?.id.toString(),
              ],
            })
          );
        }
        this.activeModal.close(true);
      });

    this.loadLegalEntity();
    this.loadRiskAssessmentToOverride();
    this.alignDisabled();
    this.setLoading(false, true);
  }

  private alignDisabled() {
    if (this.formGroup) {
      if (this.editInternalIdEnable) {
        this.formGroup.controls.internalId.enable();
      } else {
        this.formGroup.controls.internalId.disable()
      }
    }
  }

  public checkUsedField(field: string): boolean {
    return this.dialogMode === 'CREATE' || this.fields?.includes(field);
  }

  confirm() {
    const formValue = this.formGroup.getRawValue();
    const request: CreateGlobalMeasureDocRequest = {
      internalId: formValue.internalId,
      name: formValue.name,
      description: formValue.description,
      parentId: this.parentId,
      legalEntityIds: formValue.legalEntityIds,
    };

    switch (this.dialogMode) {
      case 'CREATE':
        this.store.dispatch(
          RiskAssessmentActions.createRiskAssessment({ request: request })
        );
        break;
      case 'DUPLICATE':
        this.store.dispatch(
          RiskAssessmentActions.duplicateRiskAssessment({
            id: this.duplicateRiskAssessmentPayload?.payload?.docId,
            request: request,
          })
        );
        break;
    }
  }

  private loadLegalEntity() {
    this.legalEntityService.getMenu().subscribe(
      (listLegalEntities: ListLegalEntityForMenuResponse) => {
        this.legalEntities = listLegalEntities.legalEntities;
      },
      () => {
        this.legalEntities = [];
      }
    );
  }

  private loadRiskAssessmentToOverride() {
    this.riskAssessmentsService.getToOverride().subscribe(
      (response: RiskAssessmentOverrideOnDuplicate) => {
        this.fields = Object.keys(response).filter((r) => response[r] === true);
      },
      () => {
        this.fields = [];
      }
    );
  }
}
